import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAdminMember } from '../../share/hook/useAdminMember';
import { AdminMemberEmailTarget, useAdminMemberListStore } from '../../share/store/adminMemberStore';
import MemberInfo from './MemberInfo';
import EmptyList from '../../share/component/EmptyList';
import MemberViewer from './MemberViewer';
import AdminMemberEmailViewer from './AdminMemberEmailViewer';
import { MemberData } from '../../../shared/dto/EntityRepository';
import { useAuth } from '../../../shared/hooks/useApi/useAuth';
import { useAuthTokenStore } from '../../../shared/store/authStore';
import { useScrollContainerMarqueeSelect } from '../../../shared/hooks/useScrollContainerMarqueeSelect';

const toEmailTarget = (member: MemberData): AdminMemberEmailTarget => ({
  memberId: member.memberId,
  username: member.username,
});

const getMemberItemId = (member: MemberData) => String(member.memberId);
const getMemberRowIdFromElement = (el: HTMLElement) => el.dataset.memberId;


const MemberList: React.FC = () => {
  const { user } = useAuth();
  const { authToken } = useAuthTokenStore();
  const {
    memberList,
    activeMember,
    isEmailViewerOpen,
    selectedEmailMembers,
    setSelectedEmailMembers,
    getMemberList,
    getMember,
  } = useAdminMember();
  const { pageSize, sortData, filterData } = useAdminMemberListStore();
  const [selectedId, setSelectedId] = useState<string>("");
  const [selectionAnchorId, setSelectionAnchorId] = useState<string>("");
  const prevEmailViewerOpenRef = useRef(isEmailViewerOpen);
  const memberListRef = useRef<HTMLDivElement>(null);
  const memberListCompRef = useRef<HTMLElement>(null);

  const commitMarqueeEmailTargets = useCallback((hits: MemberData[]) => {
    if (hits.length === 0) return;
    setSelectedEmailMembers(hits.map(toEmailTarget));
    setSelectionAnchorId(hits[0].memberId);
  }, [setSelectedEmailMembers]);

  const {
    handleContainerMouseDown,
    handleContainerClickCapture,
    marqueeBox,
    marqueeSelecting,
    hitItems: marqueeHitMembers,
  } = useScrollContainerMarqueeSelect({
    items: memberList,
    getItemId: getMemberItemId,
    rowSelector: '[data-member-id]',
    getRowIdFromElement: getMemberRowIdFromElement,
    containerRef: memberListCompRef,
    enabled: isEmailViewerOpen,
    onCommit: commitMarqueeEmailTargets,
  });

  const marqueeHitMemberIds = useMemo(
    () => new Set(marqueeHitMembers.map((member) => member.memberId)),
    [marqueeHitMembers]
  );

  useEffect(() => {
    if (memberListRef.current) {
      memberListRef.current.focus();
    }
  }, [memberListRef]);

  useEffect(() => {
    const getMemberListWithApi = async () => {
      if (!user) return;
      if (!authToken) return;
      console.log("getting member list...");
      await getMemberList(user.aui, { page: 1, size: pageSize, sortData: sortData, filterData: filterData });
    }
    getMemberListWithApi();
  }, [user, sortData, authToken, pageSize, filterData]);

  useEffect(() => {
    const wasEmailViewerOpen = prevEmailViewerOpenRef.current;
    prevEmailViewerOpenRef.current = isEmailViewerOpen;
    if (wasEmailViewerOpen || !isEmailViewerOpen) return;
    if (selectedEmailMembers.length === 0 && activeMember) {
      setSelectedEmailMembers([toEmailTarget(activeMember)]);
      setSelectionAnchorId(activeMember.memberId);
    }
  }, [isEmailViewerOpen, selectedEmailMembers.length, activeMember, setSelectedEmailMembers]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!memberList.length) return;
    if (isEmailViewerOpen) {
      handleEmailModeKeyDown(e);
      return;
    }
    if (e.key === 'ArrowDown') {
      const newIndex = memberList.findIndex(each => each.memberId === selectedId) + 1;
      if (newIndex < memberList.length) {
        const newId = memberList[newIndex].memberId;
        changeActiveMember(newId);
      }
    } else if (e.key === 'ArrowUp') {
      const newIndex = memberList.findIndex(each => each.memberId === selectedId) - 1;
      if (newIndex >= 0) {
        const newId = memberList[newIndex].memberId;
        changeActiveMember(newId);
      }
    }
  };

  const selectEmailMembers = (members: MemberData[]) => {
    setSelectedEmailMembers(members.map(toEmailTarget));
    if (members.length > 0) {
      setSelectionAnchorId(members[0].memberId);
      return;
    }
    setSelectionAnchorId('');
  };

  const applyShiftRange = (endMember: MemberData) => {
    const anchor = selectionAnchorId || selectedEmailMembers[0]?.memberId || activeMember?.memberId || endMember.memberId;
    const anchorIndex = memberList.findIndex((member) => member.memberId === anchor);
    const endIndex = memberList.findIndex((member) => member.memberId === endMember.memberId);
    if (anchorIndex === -1 || endIndex === -1) {
      selectEmailMembers([endMember]);
      return;
    }
    const start = Math.min(anchorIndex, endIndex);
    const end = Math.max(anchorIndex, endIndex);
    setSelectedEmailMembers(memberList.slice(start, end + 1).map(toEmailTarget));
  };

  const handleEmailModeClick = (member: MemberData, e: React.MouseEvent<HTMLElement>) => {
    if (e.shiftKey) {
      applyShiftRange(member);
      return;
    }

    if (e.metaKey || e.ctrlKey) {
      const isSelected = selectedEmailMembers.some((selected) => selected.memberId === member.memberId);
      const nextMembers = isSelected
        ? selectedEmailMembers.filter((selected) => selected.memberId !== member.memberId)
        : [...selectedEmailMembers, toEmailTarget(member)];
      setSelectedEmailMembers(nextMembers);
      setSelectionAnchorId(member.memberId);
      return;
    }

    selectEmailMembers([member]);
  };

  const handleClick = (member: MemberData, e: React.MouseEvent<HTMLElement>) => {
    if (isEmailViewerOpen) {
      handleEmailModeClick(member, e);
      return;
    }
    const selectedId = member.memberId;
    changeActiveMember(selectedId);
  };

  const changeActiveMember = (newId: string) => {
    const getMemberWithApi = async () => {
      console.log("getting member...");
      await getMember(newId);
      setSelectedId(newId);
    }
    if (newId !== selectedId) {
      getMemberWithApi();
    }
  };

  const handleEmailModeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const selectedIds = selectedEmailMembers.map((member) => member.memberId);
    const baseId = selectedIds[selectedIds.length - 1] || activeMember?.memberId || memberList[0]?.memberId;
    const baseIndex = memberList.findIndex((member) => member.memberId === baseId);
    const nextIndex = e.key === 'ArrowDown' ? baseIndex + 1 : baseIndex - 1;
    if (nextIndex < 0 || nextIndex >= memberList.length) return;
    const nextMember = memberList[nextIndex];
    if (e.shiftKey) {
      applyShiftRange(nextMember);
      return;
    }
    selectEmailMembers([nextMember]);
  };

  return (
    <MemberListContainer
      tabIndex={0}
      ref={memberListRef}
      onKeyDown={handleKeyDown}>
      <MemberListComp
        ref={memberListCompRef}
        onMouseDown={handleContainerMouseDown}
        onClickCapture={handleContainerClickCapture}
        $marqueeSelecting={marqueeSelecting}
      >
        {memberList.map((each: MemberData) =>
          <MemberInfo
            key={each.memberId}
            data={each}
            isSelected={
              isEmailViewerOpen
                ? selectedEmailMembers.some((member) => member.memberId === each.memberId) ||
                  marqueeHitMemberIds.has(each.memberId)
                : each.memberId === activeMember?.memberId
            }
            handleClick={(e) => handleClick(each, e)}
          />
        )}
        {memberList.length === 0 && <EmptyList message={"members"} />}
        {marqueeBox && (
          <MarqueeSelectionBox
            style={{
              left: marqueeBox.left,
              top: marqueeBox.top,
              width: marqueeBox.width,
              height: marqueeBox.height,
            }}
          />
        )}
      </MemberListComp>
      {isEmailViewerOpen ? <AdminMemberEmailViewer /> : <MemberViewer />}
    </MemberListContainer>
  );
};


const MemberListContainer = styled.article`
  width: 100%;
  height: calc(74vh - 2vh);
  display: flex;
  outline: none;
`;

const MemberListComp = styled.section<{ $marqueeSelecting?: boolean }>`
  flex: 1;
  width: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  position: relative;
  overflow-y: auto;
  user-select: ${({ $marqueeSelecting }) => ($marqueeSelecting ? 'none' : 'auto')};
  &::-webkit-scrollbar {
    display: none;
  }
`;

const MarqueeSelectionBox = styled.div`
  position: absolute;
  pointer-events: none;
  box-sizing: border-box;
  border: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  background: transparent;
  backdrop-filter: blur(1px);
  z-index: 3;
`;

export default MemberList;
